import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { accessControl, medicalRecords, supabase } from '../lib/supabase'
import { createClient } from '@supabase/supabase-js'

// Cliente service role para sessões de passkey
const supabaseServiceRole = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SERVICE_ROLE_KEY || ''
)
import useAccessControl from '../hooks/useAccessControl'
import { useDataSync } from '../hooks/useDataSync'
import EnhancedMedicalRecordsManager from '../components/EnhancedMedicalRecordsManager'
import AdvancedMedicalForm from '../components/AdvancedMedicalForm'
import AccessRequestForm from '../components/AccessRequestForm'
import AuditLogsViewer from '../components/AuditLogsViewer'
import PQCKeyManager from '../components/PQCKeyManager'
import PQCSecurityCenter from '../components/PQCSecurityCenter'
import PQCStatusIndicator from '../components/PQCStatusIndicator'
import PasskeyManager from '../components/PasskeyManager'
import DoctorPermissionViewer from '../components/DoctorPermissionViewer'
import { 
  Stethoscope, 
  FileText, 
  Users, 
  Shield, 
  Calendar, 
  Clock, 
  Plus, 
  Search, 
  Filter,
  Eye,
  Edit,
  CheckCircle,
  XCircle,
  AlertCircle,
  UserPlus,
  Activity,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Heart,
  Brain,
  Pill,
  TestTube,
  Microscope,
  ClipboardList,
  UserCheck,
  Bell,
  Star,
  Download,
  Share2,
  Settings,
  ChevronRight,
  PieChart,
  LineChart,
  Key,
  LogOut,
  FileCheck,
  Cpu,
  Database,
  Fingerprint
} from 'lucide-react'
import { toast } from 'sonner'

interface DashboardStats {
  totalPatients: number
  totalRecords: number
  pendingRequests: number
  todayAppointments: number
  weeklyConsultations: number
  monthlyDiagnoses: number
}

interface NewRecordForm {
  patient_id: string
  title: string
  record_type: string
  description: string
  diagnosis: string
  treatment: string
  urgency: string
  follow_up_date: string
}

interface QuickAction {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  color: string
  action: () => void
}

interface PatientSummary {
  id: string
  name: string
  lastVisit: string
  recordsCount: number
  status: 'stable' | 'attention' | 'critical'
  hasActivePermission?: boolean
  permissionType?: string
  permissionExpires?: string
}

interface MedicalRecord {
  id: string
  patient_id: string
  doctor_id: string
  title: string
  record_type: string
  description?: string
  diagnosis?: string
  treatment?: string
  urgency: string
  created_at: string
  blockchain_hash?: string
  patient?: {
    full_name: string
  }
}

interface AccessPermission {
  id: string
  patient_id: string
  doctor_id: string
  permission_type: string
  status: 'pending' | 'approved' | 'denied' | 'revoked'
  created_at: string
  granted_at?: string
  patient?: {
    full_name: string
  }
}

export default function DoctorDashboard() {
  const { user, userProfile, signOut } = useAuth()
  const { requests, permissions, requestAccess, revalidateData, invalidateAllCache } = useAccessControl()
  const { revalidateAll, registerRevalidation, forceCompleteRevalidation, hasDetectedRefresh, markDashboardProcessed, isDashboardProcessed } = useDataSync()
  const [records, setRecords] = useState<MedicalRecord[]>([])
  const [stats, setStats] = useState<DashboardStats>({
    totalPatients: 0,
    totalRecords: 0,
    pendingRequests: 0,
    todayAppointments: 0,
    weeklyConsultations: 0,
    monthlyDiagnoses: 0
  })
  // Removido dataLoading para evitar conflito com AuthContext loading
  const [showNewRecordForm, setShowNewRecordForm] = useState(false)
  const [showAdvancedForm, setShowAdvancedForm] = useState(false)
  const [editingRecord, setEditingRecord] = useState<any>(null)
  const [selectedRecord, setSelectedRecord] = useState<MedicalRecord | null>(null)
  const [showRecordDetails, setShowRecordDetails] = useState(false)
  const [newRecord, setNewRecord] = useState<NewRecordForm>({
    patient_id: '',
    title: '',
    record_type: 'consultation',
    description: '',
    diagnosis: '',
    treatment: '',
    urgency: 'normal',
    follow_up_date: ''
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null)
  const [patientSummaries, setPatientSummaries] = useState<PatientSummary[]>([])
  const [showPermissionViewer, setShowPermissionViewer] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState<{ id: string; name: string } | null>(null)

  useEffect(() => {
    if (user && userProfile?.role === 'doctor') {
      console.log('🏥 [DoctorDashboard] Carregando dados iniciais do médico...');
      loadDoctorData()
    }
  }, [user, userProfile])

  // Registrar função de revalidação no useDataSync
  useEffect(() => {
    if (user && userProfile?.role === 'doctor') {
      console.log('🔄 [DoctorDashboard] Registrando revalidação no useDataSync...');
      const unregister = registerRevalidation(async () => {
        console.log('🔄 [DoctorDashboard] Executando revalidação via useDataSync...');
        await loadDoctorData();
      });
      
      return unregister;
    }
  }, [user, userProfile, registerRevalidation])

  // Detectar refresh da página e recarregar dados (com controle robusto)
  useEffect(() => {
    if (user && userProfile?.role === 'doctor' && hasDetectedRefresh) {
      // Verificar se este dashboard já foi processado para este refresh
      if (isDashboardProcessed('doctor')) {
        console.log('⏭️ [DoctorDashboard] Refresh já processado para este dashboard, ignorando...');
        return;
      }
      
      console.log('🔄 [DoctorDashboard] Refresh detectado - recarregando dados...');
      
      // Marcar como processado imediatamente
      markDashboardProcessed('doctor');
      
      // Flag para evitar múltiplas execuções
      const lastRefresh = sessionStorage.getItem('doctor_last_refresh_toast');
      const now = Date.now();
      
      // Throttling: só mostrar toast se passou mais de 10 segundos desde o último
      const REFRESH_TOAST_THROTTLE = 10000; // 10 segundos
      const shouldShowToast = !lastRefresh || (now - parseInt(lastRefresh)) > REFRESH_TOAST_THROTTLE;
      
      const reloadAllData = async () => {
        try {
          // Limpar cache de dados
          if (invalidateAllCache) {
            invalidateAllCache();
          }
          
          // Revalidar dados do controle de acesso com cache limpo
          if (revalidateData) {
            await revalidateData(true);
          }
          
          // Recarregar dados específicos do médico
          await loadDoctorData();
          
          // Forçar revalidação completa de todos os sistemas
          await forceCompleteRevalidation();
         
         console.log('✅ [DoctorDashboard] Recarregamento completo após refresh finalizado');
         
         // Só mostrar toast se passou tempo suficiente desde o último
         if (shouldShowToast) {
           sessionStorage.setItem('doctor_last_refresh_toast', now.toString());
           toast.success('Dados atualizados após recarregamento da página');
           console.log('📢 [DoctorDashboard] Toast de refresh exibido (throttled)');
         } else {
           console.log('⏭️ [DoctorDashboard] Toast de refresh ignorado (throttling ativo)');
         }
        } catch (error) {
          console.error('❌ [DoctorDashboard] Erro ao recarregar dados após refresh:', error);
          toast.error('Erro ao atualizar dados. Tente recarregar a página.');
        }
      };
      
      reloadAllData();
    }
  }, [user, userProfile, hasDetectedRefresh, revalidateData, forceCompleteRevalidation, isDashboardProcessed, markDashboardProcessed])

  // Atualizar estatísticas quando requests ou permissions mudarem
  useEffect(() => {
    if (user && userProfile?.role === 'doctor' && requests !== undefined) {
      console.log('📊 [DoctorDashboard] Atualizando estatísticas do médico...');
      const doctorPendingRequests = requests?.filter(r => r.requester_id === user.id && r.status === 'pending').length || 0
      console.log('🔍 [DoctorDashboard] useEffect - Requests pendentes atualizados:', doctorPendingRequests)
      setStats(prevStats => ({
        ...prevStats,
        pendingRequests: doctorPendingRequests
      }))
    }
  }, [requests, permissions, user, userProfile])

  // Registrar função de carregamento com sistema de sincronização
  useEffect(() => {
    if (!user || userProfile?.role !== 'doctor') return;

    const unregister = registerRevalidation(async () => {
      console.log('🔄 Revalidação automática do DoctorDashboard...');
      await loadDoctorData();
    });

    return unregister;
  }, [user, userProfile, registerRevalidation]);

  // Revalidar dados ao navegar entre abas
  useEffect(() => {
    if (user && userProfile?.role === 'doctor') {
      console.log('🔄 Revalidando dados ao mudar aba:', activeTab);
      // Usar sistema centralizado de revalidação
      revalidateAll(true);
    }
  }, [activeTab, user, userProfile, revalidateAll])

  const loadDoctorData = async (retryCount = 0) => {
    if (!user) {
      console.log('⚠️ [DoctorDashboard] loadDoctorData: user não disponível')
      return
    }

    const maxRetries = 3
    const retryDelay = 1000 * (retryCount + 1) // 1s, 2s, 3s

    try {
      console.log(`🔄 [DoctorDashboard] Carregando dados do médico (tentativa ${retryCount + 1}/${maxRetries + 1})...`)
      console.log(`🔍 [DoctorDashboard] User ID: ${user.id}, Role: ${userProfile?.role}`)
      
      // Verificar conectividade com Supabase
      try {
        const { data: healthCheck } = await supabase.from('user_profiles').select('id').limit(1)
        console.log('✅ Conexão com Supabase OK')
      } catch (connectError) {
        console.error('❌ Erro de conectividade com Supabase:', connectError)
        throw new Error('Falha na conexão com o banco de dados')
      }
      
      // FORÇAR RECARREGAMENTO DE PERMISSÕES SE NECESSÁRIO
      if (!permissions || permissions.length === 0) {
        console.log('🔄 [DoctorDashboard] Permissões não carregadas, forçando revalidação...')
        if (revalidateData) {
          await revalidateData(true) // Limpar cache e recarregar
        }
      }
      
      // Carregar registros médicos criados pelo médico
      const { data: recordsData, error: recordsError } = await medicalRecords.getDoctorRecords(user.id)
      if (recordsError) {
        console.error('❌ Erro ao carregar registros:', recordsError)
        if (recordsError.code === '42703') {
          console.error('🔍 Erro de coluna não encontrada - verificar estrutura do banco')
          toast.error('Erro de estrutura do banco de dados. Contate o administrador.')
          return
        }
        throw recordsError
      } else {
        console.log(`✅ Registros carregados: ${recordsData?.length || 0} registros`)
        setRecords(recordsData || [])
      }

      // Calcular estatísticas avançadas - apenas versões atuais ativas
      const activeCurrentRecords = recordsData?.filter(r => 
        r.status === 'active' && (r.is_current_version === true || r.is_current_version === null)
      ) || []
      
      const totalRecords = activeCurrentRecords.length
      const uniquePatients = new Set(activeCurrentRecords.map(r => r.patient_id)).size
      // Usar requests do hook useAccessControl para contar solicitações pendentes do médico
      console.log('🔍 [DoctorDashboard] Filtrando requests para médico:', user.id)
      console.log('🔍 [DoctorDashboard] Total de requests disponíveis:', requests?.length || 0)
      
      const doctorPendingRequests = requests?.filter(r => r.requester_id === user.id && r.status === 'pending').length || 0
      console.log('🔍 [DoctorDashboard] Requests pendentes para este médico:', doctorPendingRequests)
      
      const today = new Date()
      const todayStr = today.toDateString()
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
      
      const todayAppointments = activeCurrentRecords.filter(r => 
        new Date(r.created_at).toDateString() === todayStr
      ).length
      
      const weeklyConsultations = activeCurrentRecords.filter(r => 
        new Date(r.created_at) >= weekAgo && r.record_type === 'consultation'
      ).length
      
      const monthlyDiagnoses = activeCurrentRecords.filter(r => 
        new Date(r.created_at) >= monthAgo && r.record_type === 'diagnosis'
      ).length

      setStats({
        totalPatients: uniquePatients,
        totalRecords,
        pendingRequests: doctorPendingRequests,
        todayAppointments,
        weeklyConsultations,
        monthlyDiagnoses
      })

      // Gerar resumos de pacientes baseado em permissões aprovadas
      const patientMap = new Map()
      
      // Primeiro, adicionar pacientes com permissões ativas
      console.log('🔍 [DoctorDashboard] Filtrando permissões para médico:', user.id)
      console.log('🔍 [DoctorDashboard] Total de permissões disponíveis:', permissions?.length || 0)
      console.log('🔍 [DoctorDashboard] Permissões raw:', permissions)
      
      // Detectar se é sessão de passkey e usar cliente apropriado
      let clientToUse = supabase
      let isPasskeySession = false
      
      try {
        const storedSession = localStorage.getItem('saudevirtual_passkey_session')
        if (storedSession) {
          const sessionData = JSON.parse(storedSession)
          if (sessionData.expiresAt && Date.now() < sessionData.expiresAt) {
            isPasskeySession = true
            clientToUse = supabaseServiceRole
            console.log('🔑 [DoctorDashboard] Detectada sessão de passkey, usando service role')
          }
        }
      } catch (sessionError) {
        console.warn('⚠️ [DoctorDashboard] Erro ao verificar sessão:', sessionError)
      }
      
      if (!isPasskeySession) {
        console.log('🔐 [DoctorDashboard] Sessão tradicional, usando anon key')
      }
      
      // VERIFICAÇÃO ADICIONAL: Se não há permissões, buscar diretamente do banco
      let currentPermissions = permissions
      if (!currentPermissions || currentPermissions.length === 0) {
        console.log('🔄 [DoctorDashboard] Buscando permissões diretamente do banco...')
        try {
          const { data: directPermissions, error: permError } = await clientToUse
            .from('access_permissions')
            .select('*')
            .eq('requester_id', user.id)
            .eq('status', 'approved')
          
          if (permError) {
            console.error('❌ Erro ao buscar permissões diretas:', permError)
          } else {
            currentPermissions = directPermissions || []
            console.log('✅ Permissões diretas carregadas:', currentPermissions.length)
          }
        } catch (directError) {
          console.error('❌ Erro na busca direta de permissões:', directError)
          currentPermissions = []
        }
      }
      
      const doctorPermissions = currentPermissions?.filter(p => p.requester_id === user.id && p.status === 'approved') || []
      console.log('🔍 [DoctorDashboard] Permissões aprovadas para este médico:', doctorPermissions.length)
      console.log('🔍 [DoctorDashboard] Permissões aprovadas detalhes:', doctorPermissions)
      
      // Buscar nomes reais dos pacientes da tabela user_profiles
      const patientIds = doctorPermissions.map(p => p.patient_id)
      let patientProfiles = []
      
      if (patientIds.length > 0) {
        console.log('👥 [DoctorDashboard] Buscando nomes dos pacientes:', patientIds)
        console.log('🔍 [DoctorDashboard] Usando cliente:', isPasskeySession ? 'service role' : 'anon key')
        const { data: profilesData, error: profilesError } = await clientToUse
          .from('user_profiles')
          .select('id, full_name')
          .in('id', patientIds)
        
        if (profilesError) {
          console.error('❌ Erro ao buscar perfis dos pacientes:', profilesError)
        } else {
          patientProfiles = profilesData || []
          console.log('✅ Perfis dos pacientes carregados:', patientProfiles.length)
        }
      }
      
      doctorPermissions.forEach(permission => {
        if (!patientMap.has(permission.patient_id)) {
          // Buscar nome real do paciente
          const patientProfile = patientProfiles.find(p => p.id === permission.patient_id)
          const patientName = patientProfile?.full_name || `Paciente ${permission.patient_id.slice(0, 8)}`
          
          patientMap.set(permission.patient_id, {
            id: permission.patient_id,
            name: patientName,
            lastVisit: permission.granted_at || permission.created_at,
            recordsCount: 0,
            status: 'stable' as const,
            hasActivePermission: true,
            permissionType: permission.permission_type,
            permissionExpires: permission.expires_at
          })
        }
      })
      
      // Depois, adicionar dados dos registros médicos para pacientes com acesso - apenas versões atuais ativas
      activeCurrentRecords.forEach(record => {
        if (patientMap.has(record.patient_id)) {
          const existing = patientMap.get(record.patient_id)
          existing.name = record.patient?.full_name || existing.name
          existing.recordsCount++
          if (new Date(record.created_at) > new Date(existing.lastVisit)) {
            existing.lastVisit = record.created_at
          }
        }
      })
      
      const finalPatientSummaries = Array.from(patientMap.values()).slice(0, 10)
      console.log('🔍 [DoctorDashboard] PatientSummaries finais:', finalPatientSummaries.length)
      console.log('🔍 [DoctorDashboard] PatientSummaries detalhes:', finalPatientSummaries)
      setPatientSummaries(finalPatientSummaries)
    } catch (error: any) {
      console.error(`💥 Erro ao carregar dados (tentativa ${retryCount + 1}):`, error)
      
      // Verificar se é erro de rede/conectividade
      const isNetworkError = error.message?.includes('fetch') || 
                            error.message?.includes('network') ||
                            error.message?.includes('Failed to fetch') ||
                            error.code === 'NETWORK_ERROR'
      
      if (isNetworkError && retryCount < maxRetries) {
        console.log(`🔄 Tentando novamente em ${retryDelay}ms...`)
        toast.warning(`Erro de conexão. Tentando novamente... (${retryCount + 1}/${maxRetries})`)
        
        setTimeout(() => {
          loadDoctorData(retryCount + 1)
        }, retryDelay)
        return
      }
      
      // Erro final ou não relacionado à rede
      const errorMessage = error.message || 'Erro desconhecido'
      console.error('❌ Erro final ao carregar dados:', errorMessage)
      
      if (isNetworkError) {
        toast.error('Erro de conexão persistente. Verifique sua internet e tente novamente.')
      } else {
        toast.error(`Erro ao carregar dados: ${errorMessage}`)
      }
      
      // Definir estado de fallback
      setRecords([])
      setStats({
        totalPatients: 0,
        totalRecords: 0,
        pendingRequests: 0,
        todayAppointments: 0,
        weeklyConsultations: 0,
        monthlyDiagnoses: 0
      })
      setPatientSummaries([])
    }
  }

  const quickActions: QuickAction[] = [
    {
      id: 'new-consultation',
      title: 'Nova Consulta',
      description: 'Registrar nova consulta médica',
      icon: <Stethoscope className="h-5 w-5" />,
      color: 'bg-blue-500',
      action: () => {
        setEditingRecord({
          record_type: 'consultation',
          doctor_id: user?.id
        })
        setShowAdvancedForm(true)
      }
    },
    {
      id: 'new-prescription',
      title: 'Nova Prescrição',
      description: 'Criar prescrição médica',
      icon: <Pill className="h-5 w-5" />,
      color: 'bg-green-500',
      action: () => {
        setNewRecord({ ...newRecord, record_type: 'prescription' })
        setShowNewRecordForm(true)
      }
    },
    {
      id: 'lab-results',
      title: 'Resultados de Exames',
      description: 'Registrar resultados laboratoriais',
      icon: <TestTube className="h-5 w-5" />,
      color: 'bg-purple-500',
      action: () => {
        setNewRecord({ ...newRecord, record_type: 'lab_result' })
        setShowNewRecordForm(true)
      }
    },
    {
      id: 'request-access',
      title: 'Solicitar Acesso',
      description: 'Solicitar acesso a registros de paciente',
      icon: <UserPlus className="h-5 w-5" />,
      color: 'bg-orange-500',
      action: () => {
        const patientId = prompt('Digite o ID do paciente:')
        if (patientId) {
          handleRequestAccess(patientId, 'read')
        }
      }
    }
  ]

  const handleCreateRecord = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!user || !newRecord.patient_id || !newRecord.title) {
      toast.error('Preencha todos os campos obrigatórios')
      return
    }

    try {
      const { error } = await medicalRecords.createRecord({
        patient_id: newRecord.patient_id,
        doctor_id: user.id,
        title: newRecord.title,
        record_type: newRecord.record_type,
        description: newRecord.description,
        diagnosis: newRecord.diagnosis,
        treatment: newRecord.treatment,
        is_encrypted: true
      })

      if (error) {
        toast.error('Erro ao criar registro médico')
        return
      }

      toast.success('Registro médico criado com sucesso')
      setShowNewRecordForm(false)
      setNewRecord({
        patient_id: '',
        title: '',
        record_type: 'consultation',
        description: '',
        diagnosis: '',
        treatment: '',
        urgency: 'normal',
        follow_up_date: ''
      })
      await loadDoctorData()
    } catch (error) {
      console.error('Erro ao criar registro:', error)
      toast.error('Erro ao criar registro médico')
    }
  }

  const handleRequestAccess = async (patientId: string, permissionType: string) => {
    if (!user) return

    try {
      const justification = prompt('Digite a justificativa para o acesso:') || 'Acesso necessário para atendimento médico'
      
      const result = await requestAccess(
        patientId,
        [permissionType as any], // Array de tipos de permissão
        ['all'], // Array de tipos de dados
        justification,
        '30 days', // Duração padrão
        false // Não é emergência
      )

      if (!result.success) {
        toast.error(result.error || 'Erro ao solicitar acesso')
        return
      }

      toast.success('Solicitação de acesso enviada')
      await loadDoctorData()
    } catch (error) {
      console.error('Erro ao solicitar acesso:', error)
      toast.error('Erro ao solicitar acesso')
    }
  }

  const getRecordTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      consultation: 'Consulta',
      diagnosis: 'Diagnóstico',
      prescription: 'Prescrição',
      lab_result: 'Exame Laboratorial',
      imaging: 'Exame de Imagem',
      surgery: 'Cirurgia',
      vaccination: 'Vacinação'
    }
    return labels[type] || type
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'denied':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />
      case 'revoked':
        return <XCircle className="h-5 w-5 text-gray-500" />
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />
    }
  }

  const getPatientStatusColor = (status: string) => {
    switch (status) {
      case 'critical': return 'bg-red-100 text-red-800'
      case 'attention': return 'bg-yellow-100 text-yellow-800'
      case 'stable': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const filteredRecords = records.filter(record => {
    const matchesSearch = record.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.patient?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesFilter = filterType === 'all' || record.record_type === filterType
    
    return matchesSearch && matchesFilter
  })

  // Não renderizar loading aqui - deixar o AuthContext gerenciar

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <div className="bg-green-600 p-2 rounded-lg">
                <Stethoscope className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Dashboard do Médico</h1>
                <p className="text-gray-600">Dr. {userProfile?.full_name} - {userProfile?.specialization}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <PQCStatusIndicator variant="compact" />
              <button
                onClick={signOut}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Fazer logout"
              >
                <LogOut className="h-4 w-4" />
                <span>Sair</span>
              </button>
              <button
                onClick={() => setShowNewRecordForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Novo Registro</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'overview', name: 'Visão Geral', icon: <BarChart3 className="h-5 w-5" /> },
                { id: 'patients', name: 'Pacientes', icon: <Users className="h-5 w-5" /> },
                { id: 'records', name: 'Registros Médicos', icon: <FileText className="h-5 w-5" /> },
                { id: 'access', name: 'Controle de Acesso', icon: <UserCheck className="h-5 w-5" /> },
                { id: 'audit', name: 'Auditoria LGPD', icon: <Shield className="h-5 w-5" /> },
                { id: 'analytics', name: 'Análises', icon: <PieChart className="h-5 w-5" /> },
                { id: 'passkeys', name: 'Passkeys', icon: <Fingerprint className="h-5 w-5" /> },
                { id: 'security', name: 'Segurança PQC', icon: <Cpu className="h-5 w-5" /> }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                >
                  {tab.icon}
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Pacientes</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalPatients}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <FileText className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Registros</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalRecords}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-yellow-100 p-3 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Pendentes</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.pendingRequests}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <Activity className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Hoje</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.todayAppointments}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-indigo-100 p-3 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Semana</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.weeklyConsultations}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-pink-100 p-3 rounded-lg">
                    <Brain className="h-6 w-6 text-pink-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Diagnósticos</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.monthlyDiagnoses}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={action.action}
                    className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow text-left"
                  >
                    <div className="flex items-center mb-3">
                      <div className={`${action.color} p-2 rounded-lg text-white`}>
                        {action.icon}
                      </div>
                      <h4 className="ml-3 font-medium text-gray-900">{action.title}</h4>
                    </div>
                    <p className="text-sm text-gray-600">{action.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Registros Recentes</h3>
                </div>
                <div className="p-6">
                  {records.slice(0, 5).map((record) => (
                    <div key={record.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{record.title}</p>
                        <p className="text-sm text-gray-600">{record.patient?.full_name}</p>
                        <p className="text-xs text-gray-500">{formatDate(record.created_at)}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        record.record_type === 'consultation' ? 'bg-blue-100 text-blue-800' :
                        record.record_type === 'diagnosis' ? 'bg-red-100 text-red-800' :
                        record.record_type === 'prescription' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {getRecordTypeLabel(record.record_type)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Solicitações Pendentes</h3>
                </div>
                <div className="p-6">
                  {requests?.filter(r => r.requester_id === user?.id && r.status === 'pending').slice(0, 5).map((request) => (
                    <div key={request.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Paciente ID: {request.patient_id}</p>
                        <p className="text-sm text-gray-600">{request.permission_type}</p>
                        <p className="text-xs text-gray-500">{formatDate(request.created_at)}</p>
                      </div>
                      <Clock className="h-5 w-5 text-yellow-500" />
                    </div>
                  )) || []}
                  {(!requests || requests.filter(r => r.requester_id === user?.id && r.status === 'pending').length === 0) && (
                    <p className="text-gray-500 text-center py-4">Nenhuma solicitação pendente</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Patients Tab */}
        {activeTab === 'patients' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Meus Pacientes</h2>
            </div>
            <div className="p-6">
              {(() => {
                console.log('🔍 [DoctorDashboard] Renderizando aba pacientes - patientSummaries.length:', patientSummaries.length);
                console.log('🔍 [DoctorDashboard] Renderizando aba pacientes - patientSummaries:', patientSummaries);
                return null;
              })()}
              {patientSummaries.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhum paciente encontrado</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {patientSummaries.map((patient) => (
                    <div key={patient.id} className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors ${
                      patient.hasActivePermission ? 'border-green-200 bg-green-50' : 'border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-gray-900">{patient.name}</h3>
                          {patient.hasActivePermission && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 flex items-center space-x-1">
                              <CheckCircle className="h-3 w-3" />
                              <span>Acesso Ativo</span>
                            </span>
                          )}
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPatientStatusColor(patient.status)}`}>
                          {patient.status === 'stable' ? 'Estável' :
                           patient.status === 'attention' ? 'Atenção' : 'Crítico'}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm text-gray-600">
                        <p>Última visita: {formatDate(patient.lastVisit)}</p>
                        <p>Registros: {patient.recordsCount}</p>
                        {patient.hasActivePermission && (
                          <>
                            <p className="text-green-700">Tipo de acesso: {patient.permissionType}</p>
                            {patient.permissionExpires && (
                              <p className="text-orange-600">Expira em: {formatDate(patient.permissionExpires)}</p>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex space-x-2 mt-4">
                        <button 
                          className={`flex-1 px-3 py-2 rounded text-sm transition-colors ${
                            patient.hasActivePermission 
                              ? 'bg-blue-600 text-white hover:bg-blue-700' 
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                          disabled={!patient.hasActivePermission}
                        >
                          Ver Registros
                        </button>
                        <button 
                          className="bg-gray-200 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-300 transition-colors"
                          onClick={() => {
                            setSelectedPatient({ id: patient.id, name: patient.name });
                            setShowPermissionViewer(true);
                          }}
                          title="Ver permissões de acesso"
                        >
                          <Settings className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Records Tab */}
        {activeTab === 'records' && (
          <EnhancedMedicalRecordsManager
            doctorId={user?.id}
            patientId={selectedPatientId || undefined}
            mode="doctor"
            showCreateButton={true}
            defaultView="records"
          />
        )}

        {/* Access Control Tab */}
        {activeTab === 'access' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Controle de Acesso</h2>
                <p className="text-gray-600">Gerencie suas solicitações de acesso a dados de pacientes</p>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-yellow-100 p-3 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Pendentes</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {requests?.filter(r => r.requester_id === user?.id && r.status === 'pending').length || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Aprovadas</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {permissions?.filter(p => p.requester_id === user?.id && p.status === 'approved').length || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Negadas</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {requests?.filter(r => r.requester_id === user?.id && r.status === 'denied').length || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Request Form */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Nova Solicitação de Acesso</h3>
              </div>
              <div className="p-6">
                <AccessRequestForm />
              </div>
            </div>

            {/* Pending Requests */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Minhas Solicitações Pendentes</h3>
              </div>
              <div className="p-6">
                {requests?.filter(r => r.requester_id === user?.id && r.status === 'pending').length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhuma solicitação pendente</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {requests?.filter(r => r.requester_id === user?.id && r.status === 'pending').map((request) => (
                      <div key={request.id} className="border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">Paciente ID: {request.patient_id}</p>
                            <p className="text-sm text-gray-600">Tipo: {request.permission_type}</p>
                            <p className="text-xs text-gray-500">Solicitado em: {formatDate(request.created_at)}</p>
                            {request.justification && (
                              <p className="text-sm text-gray-700 mt-2">Justificativa: {request.justification}</p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                              Pendente
                            </span>
                            <Clock className="h-5 w-5 text-yellow-500" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Active Permissions */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Permissões Ativas</h3>
              </div>
              <div className="p-6">
                {permissions?.filter(p => p.requester_id === user?.id && p.status === 'approved').length === 0 ? (
                  <div className="text-center py-8">
                    <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhuma permissão ativa</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {permissions?.filter(p => p.requester_id === user?.id && p.status === 'approved').map((permission) => (
                      <div key={permission.id} className="border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">Paciente ID: {permission.patient_id}</p>
                            <p className="text-sm text-gray-600">Tipo: {permission.permission_type}</p>
                            <p className="text-xs text-gray-500">Aprovado em: {formatDate(permission.granted_at || permission.created_at)}</p>
                            {permission.expires_at && (
                              <p className="text-xs text-gray-500">Expira em: {formatDate(permission.expires_at)}</p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Ativa
                            </span>
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Estatísticas de Registros</h3>
              </div>
              <div className="p-6">
                <div className="text-center py-8">
                  <PieChart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">Gráficos de distribuição</p>
                  <p className="text-sm text-gray-400">Análise de tipos de registros em desenvolvimento</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Tendências Mensais</h3>
              </div>
              <div className="p-6">
                <div className="text-center py-8">
                  <LineChart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">Gráficos de tendências</p>
                  <p className="text-sm text-gray-400">Análise temporal de atividades em desenvolvimento</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Passkeys Tab */}
        {activeTab === 'passkeys' && (
          <PasskeyManager userId={user?.id} />
        )}

        {/* Audit LGPD Tab */}
        {activeTab === 'audit' && (
          <AuditLogsViewer doctorId={user?.id} />
        )}

        {/* Security PQC Tab */}
        {activeTab === 'security' && (
          <PQCSecurityCenter />
        )}
      </div>

      {/* New Record Modal */}
      {showNewRecordForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Novo Registro Médico</h3>
                <button
                  onClick={() => setShowNewRecordForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
            <form onSubmit={handleCreateRecord} className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ID do Paciente *</label>
                    <input
                      type="text"
                      value={newRecord.patient_id}
                      onChange={(e) => setNewRecord({ ...newRecord, patient_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Digite o ID do paciente"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Registro</label>
                    <select
                      value={newRecord.record_type}
                      onChange={(e) => setNewRecord({ ...newRecord, record_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="consultation">Consulta</option>
                      <option value="diagnosis">Diagnóstico</option>
                      <option value="prescription">Prescrição</option>
                      <option value="lab_result">Exame Laboratorial</option>
                      <option value="imaging">Exame de Imagem</option>
                      <option value="surgery">Cirurgia</option>
                      <option value="vaccination">Vacinação</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                  <input
                    type="text"
                    value={newRecord.title}
                    onChange={(e) => setNewRecord({ ...newRecord, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Título do registro"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <textarea
                    value={newRecord.description}
                    onChange={(e) => setNewRecord({ ...newRecord, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Descrição detalhada do registro"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Diagnóstico</label>
                    <textarea
                      value={newRecord.diagnosis}
                      onChange={(e) => setNewRecord({ ...newRecord, diagnosis: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Diagnóstico médico"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tratamento</label>
                    <textarea
                      value={newRecord.treatment}
                      onChange={(e) => setNewRecord({ ...newRecord, treatment: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Tratamento prescrito"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Urgência</label>
                    <select
                      value={newRecord.urgency}
                      onChange={(e) => setNewRecord({ ...newRecord, urgency: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="low">Baixa</option>
                      <option value="normal">Normal</option>
                      <option value="high">Alta</option>
                      <option value="critical">Crítica</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Acompanhamento</label>
                    <input
                      type="date"
                      value={newRecord.follow_up_date}
                      onChange={(e) => setNewRecord({ ...newRecord, follow_up_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowNewRecordForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Criar Registro
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Details Modal */}
      {showRecordDetails && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Detalhes do Registro</h3>
                <button
                  onClick={() => setShowRecordDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                  <p className="text-gray-900">{selectedRecord.title}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <p className="text-gray-900">{getRecordTypeLabel(selectedRecord.record_type)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paciente</label>
                  <p className="text-gray-900">{selectedRecord.patient?.full_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <p className="text-gray-900">{selectedRecord.description || 'Não informado'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Diagnóstico</label>
                  <p className="text-gray-900">{selectedRecord.diagnosis || 'Não informado'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tratamento</label>
                  <p className="text-gray-900">{selectedRecord.treatment || 'Não informado'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de Criação</label>
                  <p className="text-gray-900">{formatDate(selectedRecord.created_at)}</p>
                </div>
                {selectedRecord.blockchain_hash && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hash Blockchain</label>
                    <p className="text-gray-900 font-mono text-sm break-all">{selectedRecord.blockchain_hash}</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowRecordDetails(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Fechar
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                  <Download className="h-4 w-4" />
                  <span>Baixar</span>
                </button>
                <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2">
                  <Share2 className="h-4 w-4" />
                  <span>Compartilhar</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permission Viewer Modal */}
      {selectedPatient && (
        <DoctorPermissionViewer
          isOpen={showPermissionViewer}
          onClose={() => {
            setShowPermissionViewer(false);
            setSelectedPatient(null);
          }}
          patientId={selectedPatient.id}
          patientName={selectedPatient.name}
          doctorId={user?.id || ''}
        />
      )}

      {/* Advanced Medical Form Modal */}
      {showAdvancedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto">
            <AdvancedMedicalForm
              mode="create"
              initialData={{
                ...editingRecord,
                record_type: 'consultation'
              }}
              allowPatientSelection={true}
              recordTypeFixed={true}
              onSave={async () => {
                setShowAdvancedForm(false)
                setEditingRecord(null)
                await loadDoctorData()
                toast.success('Consulta criada com sucesso!')
              }}
              onCancel={() => {
                setShowAdvancedForm(false)
                setEditingRecord(null)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}